import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Alert, CircularProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import authService from '../../services/auth.service';

const cardSx = {
  background: 'rgba(255,255,255,0.65)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(85,37,131,0.12)',
  borderRadius: '24px',
  p: { xs: 3, sm: 4 },
  width: '100%',
  maxWidth: 440,
  mx: 'auto',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    color: '#1a1a1a',
    background: 'rgba(85,37,131,0.04)',
    '& fieldset': { borderColor: 'rgba(85,37,131,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(85,37,131,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#552583' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(0,0,0,0.45)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#552583' },
};

const submitSx = {
  py: 1.8, mt: 1,
  background: 'linear-gradient(135deg, #552583 0%, #FDB927 100%)',
  color: 'white', fontWeight: 700, fontSize: '1rem',
  borderRadius: '14px', textTransform: 'none',
  '&:hover': { background: 'linear-gradient(135deg, #33154f 0%, #c98a00 100%)', transform: 'translateY(-2px)' },
  '&:disabled': { opacity: 0.6 },
};

const ForgotStep = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authService.forgotPassword(email);
      setDone(true);
    } catch {
      setError('Erreur serveur. Réessayez plus tard.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#552583', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 700, mb: 1 }}>Email envoyé</Typography>
        <Typography sx={{ color: 'rgba(0,0,0,0.5)', mb: 3, lineHeight: 1.7 }}>
          Si un compte existe pour <strong style={{ color: '#552583' }}>{email}</strong>, vous recevrez un lien dans quelques minutes.
        </Typography>
        <Button component={Link} to="/login" fullWidth sx={{ color: 'rgba(0,0,0,0.45)', textTransform: 'none', '&:hover': { color: '#552583' } }}>
          ← Retour à la connexion
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(85,37,131,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <EmailOutlinedIcon sx={{ fontSize: 32, color: '#552583' }} />
        </Box>
        <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 700, mb: 1 }}>Mot de passe oublié</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)', lineHeight: 1.7 }}>
          Entrez votre email et nous vous enverrons un lien de réinitialisation.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth sx={inputSx} />
        <Button type="submit" fullWidth variant="contained" disabled={loading} sx={submitSx}>
          {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Envoyer le lien'}
        </Button>
        <Button component={Link} to="/login" fullWidth sx={{ color: 'rgba(0,0,0,0.4)', textTransform: 'none', '&:hover': { color: '#552583' } }}>
          ← Retour à la connexion
        </Button>
      </Box>
    </>
  );
};

const ResetStep = ({ token }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    authService.verifyResetToken(token)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas'); return; }
    if (password.length < 6) { setError('Minimum 6 caractères'); return; }
    setLoading(true);
    setError('');
    try {
      await authService.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      if (err.response?.data?.passwordLeaked) {
        setError('Ce mot de passe a été détecté dans une fuite de données. Veuillez en choisir un autre.');
      } else {
        setError(err.response?.data?.message || 'Lien invalide ou expiré.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress sx={{ color: '#552583' }} /></Box>;
  }

  if (tokenValid === false) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <LockOutlinedIcon sx={{ fontSize: 56, color: '#e74c3c', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 700, mb: 1 }}>Lien invalide ou expiré</Typography>
        <Typography sx={{ color: 'rgba(0,0,0,0.5)', mb: 3 }}>
          Ce lien de réinitialisation n'est plus valide. Faites une nouvelle demande.
        </Typography>
        <Button component={Link} to="/forgot-password" fullWidth variant="contained" sx={submitSx}>
          Nouvelle demande
        </Button>
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#552583', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 700, mb: 1 }}>Mot de passe modifié</Typography>
        <Typography sx={{ color: 'rgba(0,0,0,0.5)', mb: 3 }}>
          Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
        </Typography>
        <Button component={Link} to="/login" fullWidth variant="contained" sx={submitSx}>
          Se connecter
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(85,37,131,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <LockOutlinedIcon sx={{ fontSize: 32, color: '#552583' }} />
        </Box>
        <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 700, mb: 1 }}>Nouveau mot de passe</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)', lineHeight: 1.7 }}>
          Choisissez un mot de passe sécurisé que vous n'utilisez pas ailleurs.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)' }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Nouveau mot de passe" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required fullWidth sx={inputSx} />
        <TextField label="Confirmer le mot de passe" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required fullWidth sx={inputSx} />
        <Button type="submit" fullWidth variant="contained" disabled={loading} sx={submitSx}>
          {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Réinitialiser'}
        </Button>
      </Box>
    </>
  );
};

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#E4F0F6',
        '&::before': { content: '""', position: 'absolute', top: '-30%', left: '-20%', width: '70%', height: '70%', background: 'radial-gradient(circle, rgba(85,37,131,0.07) 0%, transparent 70%)' },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
        <Box sx={cardSx}>
          {token ? <ResetStep token={token} /> : <ForgotStep />}
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
